import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Eye, Pencil, Repeat, FileQuestion, Plus, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, get, errMsg } from "@/lib/api";

const Warnings = ({ list, testId }) => (
  <div className="rounded-[6px] border border-[#E8D7B0] bg-caution-tint px-3 py-2 text-xs text-caution" role="status" data-testid={testId}>
    <p className="inline-flex items-center gap-1 font-medium"><AlertTriangle size={13} /> Fix before inviting candidates:</p>
    <ul className="ml-4 list-disc">{list.map((w) => <li key={w}>{w}</li>)}</ul>
  </div>
);

function PickRow({ t, current, onPick, busy }) {
  const bad = t.warnings?.length > 0;
  return (
    <li className={`flex flex-wrap items-center gap-3 rounded-[6px] border px-3 py-2.5 text-sm ${current ? "border-primary bg-primary/5" : "border-line"}`} data-testid={`change-row-${t.id}`}>
      <div className="min-w-[220px] flex-1">
        <p className="font-medium">{t.name}{t.job_id && <span className="ml-2 text-xs font-normal text-ink-muted">copy for this job</span>}</p>
        <p className="text-xs text-ink-secondary">{t.section_titles.join(" · ") || "No sections"}</p>
        <p className="text-xs text-ink-muted">{t.questions} questions · about {t.minutes} min</p>
        {bad && <p className="text-xs text-caution">Needs fixes: {t.warnings[0]}{t.warnings.length > 1 ? ` (+${t.warnings.length - 1} more)` : ""}</p>}
      </div>
      {current ? <span className="inline-flex items-center gap-1 text-xs text-primary"><Check size={13} /> Attached</span>
        : <Button size="sm" disabled={bad || busy} onClick={() => onPick(t)} data-testid={`change-pick-${t.id}`}>Use this</Button>}
    </li>
  );
}

function ChooseDialog({ jobId, currentId, open, onClose }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["assessment-library", "with-job"], queryFn: () => get("/assessment-library?include_job=true"), enabled: open });
  const list = (data || []).filter((t) => !t.job_id || t.job_id === jobId);
  const pick = async (t) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/assessment-template`, { template_id: t.id });
      toast.success(`"${t.name}" is now this job's assessment`);
      qc.invalidateQueries({ queryKey: ["job-assessment", jobId] }); qc.invalidateQueries({ queryKey: ["assessment-library"] });
      onClose();
    } catch (e) {
      const w = e?.response?.data?.detail?.warnings;
      toast.error(w ? `${errMsg(e)}: ${w.join("; ")}` : errMsg(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="change-assessment-list">
        <DialogHeader>
          <DialogTitle>Choose an assessment</DialogTitle>
          <DialogDescription>Everything in your library is listed. Candidates already invited keep the version they were sent.</DialogDescription>
        </DialogHeader>
        {isLoading ? <p className="text-sm text-ink-muted">Loading…</p> : !list.length
          ? <p className="text-sm text-ink-secondary" data-testid="change-empty">Your library is empty. Create an assessment to get started.</p>
          : <ul className="max-h-[55vh] space-y-2 overflow-y-auto">{list.map((t) => <PickRow key={t.id} t={t} current={t.id === currentId} onPick={pick} busy={busy} />)}</ul>}
        <div className="flex justify-between border-t border-line pt-3">
          <Button variant="outline" size="sm" onClick={() => nav(`/app/assessments/new?job=${jobId}`)} data-testid="change-create-new"><Plus size={14} /> Create new</Button>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function JobAssessmentCard({ job, staff }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [changing, setChanging] = useState(false);
  const { data } = useQuery({ queryKey: ["job-assessment", job.id], queryFn: () => get(`/jobs/${job.id}/assessment-template`) });
  const t = data?.template;
  const editForJob = async () => {
    if (t.job_id === job.id) return nav(`/app/assessments/${t.id}?job=${job.id}`);
    try {
      const r = await api.post(`/jobs/${job.id}/assessment-template`, { template_id: t.id, copy_for_job: true });
      qc.invalidateQueries({ queryKey: ["job-assessment", job.id] });
      toast.success("Made a copy for this job. The library version is unchanged.");
      nav(`/app/assessments/${r.data.template.id}?job=${job.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="panel space-y-3 p-5" data-testid="job-assessment-card" data-tour="job-assessment-card">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">Assessment for this job</p>
      {!t ? (
        <div className="flex flex-wrap items-center gap-3" data-testid="job-assessment-empty">
          <FileQuestion size={20} className="text-ink-muted" aria-hidden />
          <p className="flex-1 text-sm text-ink-secondary">No assessment attached yet. Candidates can't be invited until you choose one.</p>
          {staff && <><Button size="sm" onClick={() => setChanging(true)} data-testid="job-assessment-choose">Choose assessment</Button>
            <Button size="sm" variant="outline" onClick={() => nav(`/app/assessments/new?job=${job.id}`)} data-testid="job-assessment-create">Create new</Button></>}
        </div>
      ) : (
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-[240px] flex-1 space-y-1">
            <h3 className="text-base font-semibold" data-testid="job-assessment-name">{t.name}{t.version > 1 && <span className="ml-2 text-xs font-normal text-ink-muted">v{t.version}</span>}</h3>
            <p className="text-sm text-ink-secondary" data-testid="job-assessment-sections">{t.section_titles.join(" · ")}</p>
            <p className="inline-flex items-center gap-1 text-xs text-ink-muted"><Clock size={13} /> about {t.minutes} min · {t.questions} questions · {t.expiry_days ? `link expires after ${t.expiry_days} days` : "link doesn't expire"}</p>
            {t.warnings?.length > 0 ? <Warnings list={t.warnings} testId="job-assessment-warnings" />
              : <p className="inline-flex items-center gap-1 text-xs text-success" data-testid="job-assessment-ready"><Check size={13} /> Ready to send</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => nav(`/app/assessments/${t.id}?step=preview`)} data-testid="job-assessment-preview"><Eye size={14} /> Preview</Button>
            {staff && <Button size="sm" variant="outline" onClick={editForJob} data-testid="job-assessment-edit"><Pencil size={14} /> Edit for this job</Button>}
            {staff && <Button size="sm" onClick={() => setChanging(true)} data-testid="job-assessment-change"><Repeat size={14} /> Choose assessment</Button>}
          </div>
        </div>
      )}
      <ChooseDialog jobId={job.id} currentId={t?.id} open={changing} onClose={() => setChanging(false)} />
    </section>
  );
}
